import * as constants from '../constants';
import User from '../../models/User';
import Folder from '../../models/Folder';
import Keyword from '../../models/Keyword';
import Story from '../../models/Story';
import { Query, Platform, AnalyticsInterval, InfluencerState, Entities, Entity, SearchFilters } from '../../types';
import { ProfileSuccessResponse } from '@greatsumini/react-facebook-login';
import Board, { BoardMeta, GeoLocation } from '../../models/Board';
import Org, { OrgMember } from '../../models/Org';
import { Influencer } from '../../models/Influencer';
import { AdminBoardPosts, AdminOrg, AdminSocialIssue } from '../../models/Admin';
import Competition from '../../models/Competition';
import { getCurrentEntityType } from '../../utils/Utils';

export interface FSAInterface<Payload> {
  type: string;
  payload?: Payload;
  error?: boolean;
  meta?: any;
}

export interface SignOut {
  type: constants.SIGN_OUT;
}

export interface IncrementEnthusiasm {
  type: constants.INCREMENT_ENTHUSIASM;
}

export interface DecrementEnthusiasm {
  type: constants.DECREMENT_ENTHUSIASM;
}

export interface SetName {
  type: constants.SET_NAME;
  name: string;
}

export interface SetUser {
  type: string;
  user?: User;
}

export interface SetUserSetting {
  type: string;
  setting: string;
  value: any;
}

export interface AddFolder {
  type: constants.ADD_FOLDER;
  folder: Folder;
  currentBoardId: string;
}

export interface RemoveFolder {
  type: constants.REMOVE_FOLDER;
  folder: Folder;
  currentBoardId: string;
}

export interface BlockUsername {
  type: any;
  username: string;
  currentEntityId: string;
  socialService: string;
}

export interface RemoveBlockedUserStories {
  type: constants.REMOVE_BLOCKED_USER_STORIES;
  username: string;
  socialService: string;
}

export interface UnblockUsername {
  type: any;
  username: string;
  socialService: string;
  currentEntityId: string;
}

export interface UnblockUsername {
  type: any;
  username: string;
  socialService: string;
}

export interface SetBoardHashtagImage {
  type: constants.SET_BOARD_HASHTAG_IMAGE;
  currentBoardId: string;
  folderId: string;
}

export interface GetFolder {
  type: constants.GET_FOLDER;
  folderId: string;
  stories: Story[];
}

export interface AddKeyword {
  type: constants.ADD_KEYWORD;
  payload: Keyword;
}

export interface RemoveKeyword {
  type: constants.REMOVE_KEYWORD_REQUEST;
  payload: {
    keyword: Keyword;
  };
}

export interface SetKeywords {
  type: constants.SET_KEYWORDS;
  keywords: Keyword[];
}

export interface SetStories {
  type: constants.SET_STORIES;
  payload: Story[];
  additive?: boolean;
  total: number;
}

export interface UpdateStory {
  type: constants.UPDATE_POST_SUCCESS;
  payload: Story;
}

export interface GetStoriesRequest {
  type: constants.GET_STORIES_REQUEST;
}

export interface SetSearchQuery {
  type: constants.SET_SEARCH_QUERY;
  query: string;
}

export interface SetSearchFilters extends FSAInterface<{ filters: SearchFilters }> {
  type: constants.SET_SEARCH_FILTERS;
}

export function setSearchFilters(filters: SearchFilters): SetSearchFilters {
  return {
    type: constants.SET_SEARCH_FILTERS,
    payload: { filters },
  };
}

export interface SetSearchAll {
  type: constants.SET_SEARCH_ALL;
  searchAll: boolean;
}

export interface SetLastQuery {
  type: constants.SET_LAST_QUERY;
  lastQuery: Query;
}

export interface showRefreshButton {
  type: constants.SHOW_REFRESH_BUTTON;
  showRefreshButton: boolean;
}

export interface AddStoryToFolder {
  type: constants.ADD_STORY_TO_FOLDER;
  folderId: string;
  storyId: string;
}

export interface RemoveStoryFromFolder {
  type: constants.REMOVE_STORY_FROM_FOLDER;
  folderId: string;
  storyId: string;
  remove?: boolean;
}

export interface MarkStoryRead {
  type: constants.MARK_STORY_READ;
  storyId: string;
}

export interface DeleteStory {
  type: constants.DELETE_STORY;
  storyIds: string[];
}

export interface MoveStory {
  type: constants.MOVE_STORY;
  storyId: string;
  position: number;
}

export interface PinStory {
  type: constants.PIN_STORY;
  storyId: string;
}

export interface ClearStories {
  type: constants.CLEAR_STORIES;
}

export interface SetRecommendedStories {
  type: constants.SET_RECOMMENDED_STORIES;
  payload: Story[];
}

export interface DismissRecommendedStories {
  type: constants.DISMISS_RECOMMENDED_STORIES;
  dismissed: string[];
}

export interface SetEntities {
  type: constants.SET_ENTITIES;
  entities: Entities;
}

export interface IncrementPinnedCount {
  type: constants.INCREMENT_PINNED_COUNT;
  entityId: string;
}

export interface DecrementPinnedCount {
  type: constants.DECREMENT_PINNED_COUNT;
  entityId: string;
}

export interface SetMembers {
  type: constants.GET_MEMBER_LIST_SUCCESS;
  payload: OrgMember;
}

export interface SetMemberRole extends FSAInterface<{ _id: string; members: { [_id: string]: OrgMember } }> {
  type: constants.UPDATE_ORG_MEMBER_ROLE_SUCCESS;
  payload: {
    _id: string;
    members: {
      [_id: string]: OrgMember;
    };
  };
}

export interface RemoveOrgUser {
  type: constants.REMOVE_ORG_USER_SUCCESS;
  payload: OrgMember;
}

export interface SetBoardMemberRole {
  type: constants.UPDATE_BOARD_MEMBER_ROLE_SUCCESS;
  member: OrgMember;
}

export interface RemoveBoardMember {
  type: constants.REMOVE_BOARD_MEMBER_SUCCESS;
  member: OrgMember;
}

export interface SetNewOrgMembers {
  type: constants.INVITE_ORG_MEMBERS_SUCCESS;
  members: OrgMember[];
}

export interface SetNewEntityMembers {
  type: constants.INVITE_BOARD_MEMBERS_SUCCESS | constants.INVITE_COMPETITION_MEMBERS_SUCCESS;
  members: OrgMember[];
}

export interface DeleteEntity {
  type: constants.DELETE_BOARD_SUCCESS | constants.DELETE_COMPETITION_SUCCESS;
  payload: {
    entityId: string;
  };
}

export interface UpdatePaymentInfo {
  type: constants.UPDATE_PAYMENT_INFO_SUCCESS;
}

export interface attachPromoCode {
  type: constants.ATTACH_PROMO_CODE_SUCCESS;
}

export interface SetEntity {
  type: constants.GET_BOARD_SUCCESS | constants.GET_COMPETITION_SUCCESS;
  payload: Entity;
}

export interface SetNewGeotag {
  type: constants.ADD_GEOTAG_URL_SUCCESS;
  payload: GeoLocation;
}

export interface RemoveGeotag {
  type: constants.REMOVE_GEOTAG_URL_SUCCESS;
  payload: { id: string };
}

export interface SetOrg {
  type: constants.GET_ORG_SUCCESS;
  payload: Org;
}

export interface SetToken {
  type: constants.SET_FB_TOKEN_SUCCESS | constants.SET_YOUTUBE_ACC_TOKEN_SUCCESS;
  payload: {
    board: Board;
    message: string;
  };
}

export interface SetInfToken {
  type: constants.INF_SET_FB_TOKEN_SUCCESS;
  payload: {
    influencer: Influencer;
    message: string;
  };
}

export interface ClearToken {
  type: constants.CLEAR_SOCIAL_TOKEN_SUCCESS | constants.INF_CLEAR_SOCIAL_TOKEN_SUCCESS;
  payload: {
    network: string;
  };
}

export interface SetAssociatedFBPage {
  type: constants.SET_ASSOCIATED_FBPAGE_SUCCESS | constants.INF_SET_ASSOCIATED_FBPAGE_SUCCESS;
  payload: {
    socialNetworks: {};
    _id: string;
  };
}

export interface SetSubscribedInstagramMentions {
  type: constants.SET_SUBSCRIBE_INSTAGRAM_MENTIONS_REQUEST | constants.SET_SUBSCRIBE_INSTAGRAM_MENTIONS_SUCCESS;
  payload: {
    subscribe: boolean;
  };
}

export interface SetAnalyticsDateRange {
  type: constants.SET_ANALYTICS_DATE_RANGE;
  payload: {
    from: Date;
    to: Date;
  };
}

export interface SetAnalyticsPlatforms {
  type: constants.SET_ANALYTICS_PLATFORMS;
  payload: {
    platforms: Platform[];
  };
}

export interface SetAnalyticsInterval {
  type: constants.SET_ANALYTICS_INTERVAL;
  payload: {
    interval: AnalyticsInterval;
  };
}

export interface BlockWords {
  type: constants.BLOCK_BOARD_WORDS_REQUEST | constants.BLOCK_COMPETITION_WORDS_REQUEST;
  payload: {
    blockedWords: string[];
  };
}

export interface UnblockWord {
  type: constants.UNBLOCK_BOARD_WORDS_REQUEST | constants.UNBLOCK_COMPETITION_WORDS_REQUEST;
  payload: {
    unblockedWord: string;
  };
}

export interface RemoveRequiredWord {
  type: constants.REMOVE_REQUIRED_BOARD_WORD_REQUEST | constants.REMOVE_REQUIRED_COMPETITION_WORD_REQUEST;
  payload: {
    removedWord: string;
  };
}

export interface ToggleWordBlacklist {
  type: constants.TOGGLE_BOARD_WORD_BLACKLIST_REQUEST | constants.TOGGLE_COMPETITION_WORD_BLACKLIST_REQUEST;
  payload: {
    active: boolean;
  };
}

export interface RequireWords {
  type: constants.REQUIRE_BOARD_WORDS_REQUEST | constants.REQUIRE_COMPETITION_WORDS_REQUEST;
  payload: {
    requiredWords: string[];
  };
}

export interface PinStory {
  type: constants.PIN_STORY;
  storyId: string;
}

export interface SetInfluencer {
  type: constants.SUBMIT_INFLUENCER_PERSONAL_SUCCESS;
  payload: InfluencerState;
}

export interface HandleNewStories {
  type: constants.HANDLE_NEW_STORIES;
  payload: {
    data: any;
    folderName: string;
  };
}

export interface ClearStoryPushInfo {
  type: constants.CLEAR_STORY_PUSH_INFO;
}

export interface SetAdminSummary {
  type: constants.ADMIN_SYSTEM_SUMMARY_SUCCESS;
  payload: AdminSocialIssue[];
}

export interface SetAdminBoardsSummary {
  type: constants.ADMIN_ORG_GET_BOARDS_SUMMARY_SUCCESS;
  payload: {
    largestBoards: AdminBoardPosts[];
  };
}

export interface SetAdminOrgPage {
  type: constants.ADMIN_ORG_PAGE_SUCCESS;
  payload: {
    data: AdminOrg;
  };
}

export interface SetAdminOrgsList {
  type: constants.ADMIN_ORGS_LIST_SUCCESS;
  payload: {
    orgs: AdminOrg[];
    total: number;
  };
}

export interface AdminModifyBoardOnOrg {
  type: constants.ADMIN_BOARD_META_MODIFY_SUCCESS;
  payload: {
    data: {
      _id: string;
      metadata: BoardMeta;
    };
  };
}

export interface AdminModifyCompetitionOnOrg {
  type: constants.ADMIN_COMPETITION_META_MODIFY_SUCCESS;
  payload: {
    data: {
      _id: string;
      metadata: any;
    };
  };
}

export interface AdminModifyBoardOnOrgOpt {
  type: constants.ADMIN_BOARD_META_MODIFY_REQUEST;
  payload: {
    _id: string;
    metadata: BoardMeta;
  };
}

export interface AdminModifyCompetitionOnOrgOpt {
  type: constants.ADMIN_BOARD_META_MODIFY_REQUEST;
  payload: {
    _id: string;
    metadata: BoardMeta;
  };
}

export interface SetCompetitions {
  type: constants.SET_COMPETITIONS;
  competitions: Competition[];
}

export interface SetEntityMembers {
  type: constants.GET_COMPETITION_MEMBER_LIST_SUCCESS | constants.GET_BOARD_MEMBER_LIST_SUCCESS;
  payload: OrgMember[];
}

export interface ApproveEntry {
  type: constants.APPROVE_COMPETITION_ENTRY_REQUEST;
  payload: {
    postIds: string[];
    accepted: boolean;
    stageNum: number;
  };
}

export interface RemoveEntryStage {
  type: constants.FORCE_REMOVE_ENTRY_STAGE_REQUEST;
  payload: {
    postIds: string[];
    stageToRemove: number;
  };
}

export interface SetNetworkCommentPostOpt {
  type: constants.SET_COMMENT_POST_REQUEST;
  payload: {
    postId: string;
    postLink: string;
    network: string;
  };
}

export interface SetNetworkCommentPost {
  type: constants.SET_COMMENT_POST_SUCCESS;
  payload: {
    socialNetworks: any;
    network: string;
  };
}

export interface SetBoard {
  type: constants.SET_BOARD;
  payload: Board;
}

export interface SetCompetition {
  type: constants.SET_COMPETITION;
  payload: Competition;
}

export function setCompetition(competition: Competition): SetCompetition {
  return {
    type: constants.SET_COMPETITION,
    payload: competition,
  };
}

export function setBoard(board: Board): SetBoard {
  return {
    type: constants.SET_BOARD,
    payload: board,
  };
}

export function newInboxNextStep() {
  return {
    type: constants.NEW_INBOX_NEXT_STEP,
  };
}

export function newInboxPreviousStep() {
  return { type: constants.NEW_INBOX_PREV_STEP };
}

export function clearStoryPushInfo() {
  return {
    type: constants.CLEAR_STORY_PUSH_INFO,
  };
}

export function handleNewStories(data: any, folderId: string) {
  return {
    type: constants.HANDLE_NEW_STORIES,
    payload: {
      data,
      folderId,
    },
  };
}

export function setAnalyticsInterval(interval: AnalyticsInterval) {
  return {
    type: constants.SET_ANALYTICS_INTERVAL,
    payload: {
      interval,
    },
  };
}

export function setAnalyticsPlatforms(platforms: Platform[]) {
  return {
    type: constants.SET_ANALYTICS_PLATFORMS,
    payload: {
      platforms,
    },
  };
}

export function setAnalyticsDateRange(from: Date, to: Date) {
  return {
    type: constants.SET_ANALYTICS_DATE_RANGE,
    payload: {
      from,
      to,
    },
  };
}

export function incrementPinnedCount(entityId: string) {
  return {
    type: constants.INCREMENT_PINNED_COUNT,
    entityId,
  };
}

export function decrementPinnedCount(entityId: string) {
  return {
    type: constants.DECREMENT_PINNED_COUNT,
    entityId,
  };
}

export type EnthusiasmAction = IncrementEnthusiasm | DecrementEnthusiasm | SetName;

export function dismissRecommendedStories(dismissed: string[]): DismissRecommendedStories {
  return {
    dismissed,
    type: constants.DISMISS_RECOMMENDED_STORIES,
  };
}

export function signOut() {
  return {
    type: constants.SIGN_OUT,
  };
}

export interface SetFacebookAuth {
  facebookAuth: ProfileSuccessResponse;
  type: constants.SET_FACEBOOK_AUTH;
}

export function setFacebookAuth(res: ProfileSuccessResponse) {
  return {
    facebookAuth: res,
    type: constants.SET_FACEBOOK_AUTH,
  };
}

export function clearStories() {
  return {
    type: constants.CLEAR_STORIES,
  };
}

export function incrementEnthusiasm(): IncrementEnthusiasm {
  return {
    type: constants.INCREMENT_ENTHUSIASM,
  };
}

export function decrementEnthusiasm(): DecrementEnthusiasm {
  return {
    type: constants.DECREMENT_ENTHUSIASM,
  };
}

export function setName(name: string): SetName {
  return {
    name,
    type: constants.SET_NAME,
  };
}

export function setUser(user?: User): SetUser {
  return {
    user,
    type: constants.SET_USER,
  };
}

export function setUserSetting(setting: string, value: any) {
  return {
    setting,
    value,
    type: constants.SET_USER_SETTING,
  };
}

export function addFolder(folder: Folder, currentBoardId: string): AddFolder {
  return {
    folder,
    currentBoardId,
    type: constants.ADD_FOLDER,
  };
}

export function removeFolder(folder: Folder, currentBoardId: string): RemoveFolder {
  return {
    folder,
    currentBoardId,
    type: constants.REMOVE_FOLDER,
  };
}

export function getFolder(folderId: string, stories: Story[]): GetFolder {
  return {
    folderId,
    stories,
    type: constants.GET_FOLDER,
  };
}

export function addStoryToFolder(folderId: string, storyId: string): AddStoryToFolder {
  return {
    folderId,
    storyId,
    type: constants.ADD_STORY_TO_FOLDER,
  };
}

export function removeStoryFromFolder(folderId: string, storyId: string, remove: boolean): RemoveStoryFromFolder {
  return {
    folderId,
    storyId,
    remove,
    type: constants.REMOVE_STORY_FROM_FOLDER,
  };
}

export function markStoryRead(storyId: string): MarkStoryRead {
  return {
    storyId,
    type: constants.MARK_STORY_READ,
  };
}

export function deleteStory(storyIds: string[]): DeleteStory {
  return {
    storyIds,
    type: constants.DELETE_STORY,
  };
}

export function moveStory(storyId: string, position: number): MoveStory {
  return {
    storyId,
    position,
    type: constants.MOVE_STORY,
  };
}

export function addKeyword(keyword: Keyword): AddKeyword {
  return {
    payload: keyword,
    type: constants.ADD_KEYWORD,
  };
}

export function removeKeyword(keyword: Keyword): RemoveKeyword {
  return {
    payload: {
      keyword,
    },
    type: constants.REMOVE_KEYWORD_REQUEST,
  };
}

export function setKeywords(keywords: Keyword[]): SetKeywords {
  return {
    keywords,
    type: constants.SET_KEYWORDS,
  };
}

export function blockUsername(username: string, socialService: string, currentEntityId: string): BlockUsername {
  const entityType = getCurrentEntityType();
  return {
    username,
    socialService,
    currentEntityId,
    type: constants.EntityConstFactory[entityType].entityBlockUsername,
  };
}

export function removeBlockedUserStories(username: string, socialService: string): RemoveBlockedUserStories {
  return {
    username,
    socialService,
    type: constants.REMOVE_BLOCKED_USER_STORIES,
  };
}

export function unblockUsername(username: string, socialService: string, currentEntityId: string): UnblockUsername {
  const entityType = getCurrentEntityType();
  return {
    username,
    socialService,
    currentEntityId,
    type: constants.EntityConstFactory[entityType].entityUnblockUsername,
  };
}

export function setBoardHashtagImage(currentBoardId: string, folderId: string): SetBoardHashtagImage {
  return {
    type: constants.SET_BOARD_HASHTAG_IMAGE,
    currentBoardId,
    folderId,
  };
}

export function setStories(payload: Story[], total?: number, additive?: boolean): SetStories {
  return {
    payload,
    additive,
    total: 0,
    type: constants.SET_STORIES,
  };
}

export function setSearchQuery(query: string): SetSearchQuery {
  return {
    query,
    type: constants.SET_SEARCH_QUERY,
  };
}

export function setLastQuery(lastQuery: Query): SetLastQuery {
  return {
    lastQuery,
    type: constants.SET_LAST_QUERY,
  };
}

export function setSearchAll(searchAll: boolean): SetSearchAll {
  return {
    searchAll,
    type: constants.SET_SEARCH_ALL,
  };
}

export function showRefreshButton() {
  return {
    type: constants.SHOW_REFRESH_BUTTON,
  };
}

export function setEntities(entities: Entities): SetEntities {
  return {
    type: constants.SET_ENTITIES,
    entities,
  };
}

export function clearEntity() {
  return {
    type: constants.CLEAR_ENTITY,
  };
}

export function pinStory(storyId: string): PinStory {
  return {
    storyId,
    type: constants.PIN_STORY,
  };
}

export function setCompetitions(competitions: Competition[]): SetCompetitions {
  return {
    type: constants.SET_COMPETITIONS,
    competitions,
  };
}

export function clearNewInbox() {
  return {
    type: constants.NEW_INBOX_CLEAR,
  };
}
